import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";
import { Surface } from "./Surface";
import { Text } from "./Text";

/**
 * A focused confirmation modal (portal + ink scrim + paper card). Used for
 * irreversible actions — deleting a conversation, clearing history. The confirm
 * button is ink by default; pass `danger` for destructive flows so the confirm
 * renders as the red danger fill.
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Disables the buttons while the action is in flight. */
  busy?: boolean;
  /** Renders the confirm button as the destructive (red) variant. */
  danger?: boolean;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  busy = false,
  danger = false,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) onCancel();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={() => !busy && onCancel()}
        className="absolute inset-0 cursor-default bg-ink/30 backdrop-blur-[2px]"
      />
      <Surface
        tone="paper"
        radius="lg"
        border="hairline"
        shadow="pill"
        className="relative flex w-full max-w-[420px] flex-col gap-3 p-6"
      >
        <Text variant="h4" as="h2">
          {title}
        </Text>
        <Text variant="body" className="text-ink-soft">
          {message}
        </Text>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={danger ? "danger" : "ink"}
            size="sm"
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </Surface>
    </div>,
    document.body,
  );
}
